'use client'

import { memo, type ReactNode } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'

/** HUD status box: top-left game overlay style */
export const Ninxl8BitHud = memo(function Ninxl8BitHud({
  lines,
  className,
  showCursor = false,
}: {
  lines: string[]
  className?: string
  showCursor?: boolean
}) {
  return (
    <div className={cn('ninxl-hud-panel', showCursor && 'ninxl-hud-cursor', className)}>
      {lines.map((line) => (
        <div key={line}>{line}</div>
      ))}
    </div>
  )
})

/** Pixel-bordered frame for sections and cards */
export const Ninxl8BitFrame = memo(function Ninxl8BitFrame({
  children,
  className,
  label,
  flush = false,
}: {
  children: ReactNode
  className?: string
  label?: string
  /** No inner padding: cipher / full-bleed panels set their own */
  flush?: boolean
}) {
  return (
    <div
      className={cn(
        'ninxl-pixel-border ninxl-theme-bg-frame relative',
        flush ? 'p-0' : 'p-6 md:p-8',
        className,
      )}
    >
      {label ? (
        <div className="ninxl-8bit-label ninxl-theme-bg-frame ninxl-theme-text absolute -top-3 left-4 z-20 px-2">
          {label}
        </div>
      ) : null}
      {children}
    </div>
  )
})

/** Section wrapper with 8-bit styling */
export const Ninxl8BitSection = memo(function Ninxl8BitSection({
  children,
  className,
  alt = false,
  id,
  'data-section': dataSection,
}: {
  children: ReactNode
  className?: string
  alt?: boolean
  id?: string
  'data-section'?: string
}) {
  return (
    <section
      id={id}
      data-section={dataSection ?? id}
      className={cn(
        'ninxl-section-8bit relative py-16 md:py-20',
        alt && 'ninxl-section-8bit-alt',
        className
      )}
    >
      {children}
    </section>
  )
})

/** Pixel divider between sections: dots pulse in sequence */
export const Ninxl8BitDivider = memo(function Ninxl8BitDivider() {
  const shouldReduce = useReducedMotion()

  return (
    <div className="mx-auto flex max-w-4xl items-center gap-4 px-6 py-6" aria-hidden>
      <div className="ninxl-theme-border-soft ninxl-divider-line h-[3px] flex-1 bg-[color:var(--ninxl-theme-border-soft)]" />
      <div className="flex gap-1">
        {[0, 1, 2].map((i) =>
          shouldReduce ? (
            <div
              key={i}
              className={cn(
                'h-2 w-2',
                i === 1
                  ? 'bg-[color:var(--ninxl-brand-teal)]'
                  : 'ninxl-theme-divider-dot bg-[color:var(--ninxl-8bit-border)]',
              )}
            />
          ) : (
            <motion.div
              key={i}
              className={cn(
                'h-2 w-2',
                i === 1
                  ? 'bg-[color:var(--ninxl-brand-teal)]'
                  : 'ninxl-theme-divider-dot bg-[color:var(--ninxl-8bit-border)]',
              )}
              animate={{ opacity: [0.35, 1, 0.35], scale: [1, 1.25, 1] }}
              transition={{
                duration: 1.6,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: i * 0.22,
              }}
            />
          ),
        )}
      </div>
      <div className="ninxl-theme-border-soft ninxl-divider-line h-[3px] flex-1 bg-[color:var(--ninxl-theme-border-soft)]" />
    </div>
  )
})
